import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LogOut, UserRoundSearch } from 'lucide-react';
import { Button } from '@/components/ui';
import { securityApi } from '@/features/platform/api/security';
import { describeError } from '@/lib/api';
import { useSession } from '@/lib/session';

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'expired';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/**
 * Sticky, high-contrast banner shown on every screen while a platform operator is impersonating.
 * The data comes from the server session (`/auth/session → impersonation`); the banner cannot start,
 * extend or fake an impersonation. "Exit" asks the server to end it and hand back the operator session.
 */
export function ImpersonationBanner() {
  const session = useSession();
  const qc = useQueryClient();
  const imp = session.impersonation;
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!imp) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [imp]);

  if (!imp || !session.user) return null;
  const remaining = imp.expiresAt ? new Date(imp.expiresAt).getTime() - now : null;

  const exit = async () => {
    setBusy(true);
    setError(null);
    try {
      await qc.cancelQueries();
      const r = await securityApi.exitImpersonation();
      // Nothing cached as the impersonated user may survive the switch back.
      await qc.cancelQueries();
      qc.removeQueries({ predicate: query => query.queryKey[0] !== 'session' });
      qc.getMutationCache().clear();
      window.location.replace(r.restored ? '/platform/impersonation' : '/login');
    } catch (e) {
      setError(describeError(e).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="region"
      aria-label="Impersonation in progress"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 60,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px 16px',
        padding: '8px 16px',
        background: '#1f1300',
        color: '#fff7e0',
        borderBottom: '3px solid #f5a524',
        fontSize: '0.875rem',
      }}
    >
      <UserRoundSearch size={18} aria-hidden style={{ color: '#f5a524', flex: 'none' }} />
      <span style={{ flex: '1 1 18rem', minWidth: 0 }}>
        <strong>Read-only preview: {session.user.name}</strong> ({session.user.email}) as <strong>{imp.impersonator.name}</strong>
        <span> · Browse their sidebar and dashboard. Changes are disabled.</span>
        <span style={{ opacity: 0.85 }}> · Reason: {imp.reason}</span>
      </span>
      {remaining !== null && (
        <span aria-live="off" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }} title={imp.expiresAt ?? undefined}>
          {remaining > 0 ? `Ends in ${formatRemaining(remaining)}` : 'Session expired'}
        </span>
      )}
      {error && (
        <span role="alert" style={{ color: '#ffb4a8' }}>
          {error}
        </span>
      )}
      <Button size="sm" variant="primary" icon={<LogOut size={14} />} onClick={() => void exit()} loading={busy} loadingText="Exiting…">
        Exit impersonation
      </Button>
    </div>
  );
}
